import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { EntityListPageProps } from "./EntityListPage";

/** A sort offered on a list page: its i18n label and how to order two rows. */
export interface ListSort<T> {
  value: string;
  label: string;
  compare: (a: T, b: T) => number;
}

/** Shared comparators for the common sorts. */
export const byName = <T extends { name: string }>(a: T, b: T) => a.name.localeCompare(b.name);
export const byNewest = <T extends { createdAt: string }>(a: T, b: T) => b.createdAt.localeCompare(a.createdAt);

/**
 * The search + All/Mine filter + sort state every Build list page shares.
 * Pages own only what differs: what "Mine" means and which sorts they offer.
 *
 * `search` is returned before any rows exist so pages that search on the
 * server can put it in their query; those call `apply(rows, { searched: true })`
 * so the rows aren't filtered by name a second time.
 */
export function useListControls<T extends { name: string }>({
  isMine,
  sorts,
}: Readonly<{ isMine: (item: T) => boolean; sorts: ReadonlyArray<ListSort<T>> }>) {
  const { t } = useTranslation("build");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState(sorts[0]?.value ?? "");

  const apply = (items: readonly T[] | undefined, { searched = false } = {}): T[] => {
    const term = searched ? "" : search.trim().toLowerCase();
    const visible = (items ?? []).filter(
      (item) => (!term || item.name.toLowerCase().includes(term)) && (filter !== "mine" || isMine(item)),
    );
    const compare = sorts.find((s) => s.value === sort)?.compare;
    return compare ? visible.sort(compare) : visible;
  };

  const listProps: Pick<EntityListPageProps, "search" | "onSearch" | "filter" | "sort"> = {
    search,
    onSearch: setSearch,
    filter: {
      label: t("list.show"),
      value: filter,
      onChange: setFilter,
      options: [
        { value: "all", label: t("list.filter.all") },
        { value: "mine", label: t("list.filter.mine") },
      ],
    },
    sort: {
      label: t("list.sort.label"),
      value: sort,
      onChange: setSort,
      options: sorts.map(({ value, label }) => ({ value, label })),
    },
  };

  return { search, apply, listProps };
}
